import React, { useEffect, useState } from "react";
import { Layout, Tabs, Modal, Typography } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import Patient from "../patientDetails";
import Orders from "../orders";
import Absences from "./Absences";
import PatientPersonalFile from "../patientPersonalFile";
import Scheduling from "../scheduling";
import WorkDiaries from "../workDiaries";
import PatientLogs from "../logs/PatientLogs";
import PatientDocuments from "../patientDocuments";
import { useIntl } from "react-intl";
import HouseVisits from "../houseVisits";
import PropTypes from "prop-types";
import { loadRecord, reset } from "../patientDetails/patientDetailsSlice";
import { useDispatch, useSelector } from "react-redux";
import { unsavedChangesConfirmMessage } from "../../components/forms/formsFeedback";
import NavigationPrompt from "react-router-navigation-prompt";
import { usePermission } from "../../hooks/accessControl";
import Activities from "../activities";
import { getFullName } from "../../components/util/helpers";

const { Text } = Typography;

const PatientDetails = (props) => {
  let { branchId, id } = useParams();
  const organizationDetails = useSelector(
    (state) => state.organization.details
  );
  const dispatch = useDispatch();
  let location = useLocation();
  const [activeTab, setActiveTab] = useState("information");
  const hasChanges = useSelector(
    (state) => state.patientDetails.changedItems.changed.length > 0
  );
  const intl = useIntl();
  const [modal, contextHolder] = Modal.useModal();
  const activePatient = useSelector(
    (state) => state.patientDetails.activePatient
  );
  useEffect(() => {
    getPatient();
    return function cleanup() {
      dispatch(reset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      location.state &&
      location.state.activeTab &&
      location.state.activeTab === "scheduling"
    ) {
      onChange("scheduling");
    }
  }, [location.state]);

  const getPatient = () => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: branchId,
      admin: isAdmin,
    };
    dispatch(loadRecord(params));
  };

  const components = [
    {
      key: "information",
      titleId: "app.patients.information",
      comp: (
        <div>
          <Patient />
        </div>
      ),
    },
    {
      key: "absences",
      titleId: "app.patients.absences",
      comp: <Absences />,
    },
    {
      key: "documentation",
      titleId: "app.patients.personal_file",
      comp: <PatientPersonalFile />,
    },

    {
      key: "service_orders",
      titleId: "app.patients.service_orders",
      comp: <Orders />,
      disabled: !activePatient?.local_id_number,
    },
    {
      key: "scheduling",
      titleId: "app.patients.scheduling",
      comp: (
        <div style={{ height: "100%", display: "flex" }}>
          <Scheduling activeTab={activeTab} />
        </div>
      ),
      disabled: !activePatient?.local_id_number,
    },
    {
      key: "work_diary",
      titleId: "app.patients.work_diary",
      comp: <WorkDiaries />,
      disabled: !activePatient?.local_id_number,
    },
    {
      key: "house_visits",
      titleId: "app.patients.house_visits",
      comp: <HouseVisits />,
      disabled: !activePatient?.local_id_number,
    },
  ];

  const hasAssetsPermission = usePermission(branchId, "assets", "viewer");
  if (hasAssetsPermission) {
    components.push({
      key: "documents",
      titleId: "app.patients.documents",
      comp: <PatientDocuments />,
    });
  }

  components.push({
    key: "logs",
    titleId: "app.patients.logs",
    comp: <PatientLogs activeTab={activeTab} />,
  });
  const allowedFeatures =
    organizationDetails.features &&
    organizationDetails.features.map((i) => i.name);
  const crmIsAllowed = allowedFeatures?.includes("crm");
  const hasCrmViewerPermission = usePermission(branchId, "crm", "viewer");
  if (crmIsAllowed && hasCrmViewerPermission) {
    components.splice(2, 0, {
      key: "activities",
      titleId: "app.patients.activities",
      comp: (
        <Activities
          entity={"patients"}
          entityId={activePatient.id}
          branchId={branchId || activePatient?.branch}
          entityName={getFullName(
            activePatient.surename,
            activePatient.first_name
          )}
          reportId={
            activePatient.local_id_number
              ? `תעודת זהות - ${activePatient.local_id_number}`
              : ""
          }
          hasEmail={
            activePatient?.contact_info?.email &&
            activePatient?.contact_info?.agree_to_receive_email === true
          }
        />
      ),
    });
  }

  const navigateAndLoad = async (key, params) => {
    setActiveTab(key);
    dispatch(loadRecord(params));
  };

  const onChange = async (key) => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      id: id,
      branchId: branchId,
      admin: isAdmin,
    };

    if (hasChanges) {
      await unsavedChangesConfirmMessage({
        intl,
        onOk: () => {
          navigateAndLoad(key, params);
        },
        onCancel: () => {},
        modal,
      });
      return;
    }
    navigateAndLoad(key, params);
  };

  return (
    <Layout style={{ zIndex: 5, background: "transparent" }}>
      <NavigationPrompt when={hasChanges}>
        {({ onConfirm, onCancel }) => {
          return (
            <Modal
              title={intl.formatMessage({
                id: "app.forms.unSavedChangesTitle",
              })}
              open={true}
              onOk={() => {
                onConfirm();
              }}
              onCancel={onCancel}
              okText={intl.formatMessage({
                id: "app.forms.unSavedChangesOkText",
              })}
              cancelText={intl.formatMessage({
                id: "app.forms.unSavedChangesCancelText",
              })}
            >
              <Text>
                {intl.formatMessage({
                  id: "app.forms.unSavedChangesContent",
                })}
              </Text>
            </Modal>
          );
        }}
      </NavigationPrompt>
      {contextHolder}
      <Tabs
        defaultActiveKey="information"
        activeKey={activeTab}
        animated={false}
        onChange={onChange}
        items={components.map((option) =>
          !option.hide
            ? {
                label: <FormattedMessage id={option.titleId} />,
                key: option.key,
                children: <>{option.comp}</>,
                disabled: option.disabled,
              }
            : null
        )}
      />
    </Layout>
  );
};

PatientDetails.propTypes = {
  mode: PropTypes.string,
};

export default PatientDetails;
